import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import type { User } from '@peles/shared';
import * as authApi from '../api/auth';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  realUser: User | null;
  isImpersonating: boolean;
}

type AuthAction =
  | { type: 'SET_LOADING' }
  | { type: 'SET_USER'; user: User }
  | { type: 'CLEAR_USER' }
  | { type: 'START_IMPERSONATION'; impersonatedUser: User; realUser: User }
  | { type: 'STOP_IMPERSONATION'; realUser: User };

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: true };
    case 'SET_USER':
      return { ...state, user: action.user, isLoading: false, isAuthenticated: true };
    case 'CLEAR_USER':
      return {
        user: null,
        isLoading: false,
        isAuthenticated: false,
        realUser: null,
        isImpersonating: false,
      };
    case 'START_IMPERSONATION':
      return {
        ...state,
        user: action.impersonatedUser,
        realUser: action.realUser,
        isImpersonating: true,
        isLoading: false,
        isAuthenticated: true,
      };
    case 'STOP_IMPERSONATION':
      return {
        ...state,
        user: action.realUser,
        realUser: null,
        isImpersonating: false,
        isLoading: false,
        isAuthenticated: true,
      };
  }
}

interface AuthContextValue extends AuthState {
  loginWithGoogle: () => Promise<void>;
  handleGoogleCallback: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  impersonate: (userId: string) => Promise<void>;
  stopImpersonation: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, {
    user: null,
    isLoading: true,
    isAuthenticated: false,
    realUser: null,
    isImpersonating: false,
  });

  // Load user on mount if tokens exist
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      dispatch({ type: 'CLEAR_USER' });
      return;
    }

    const realToken = localStorage.getItem('real_access_token');

    authApi
      .getMe()
      .then(async (user) => {
        if (realToken) {
          // Restore impersonation state
          const realUser = await authApi.getRealUser();
          if (realUser) {
            dispatch({ type: 'START_IMPERSONATION', impersonatedUser: user, realUser });
          } else {
            // Real token is invalid — clean up and treat as normal session
            localStorage.removeItem('real_access_token');
            localStorage.removeItem('real_refresh_token');
            dispatch({ type: 'SET_USER', user });
          }
        } else {
          dispatch({ type: 'SET_USER', user });
        }
      })
      .catch(() => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('real_access_token');
        localStorage.removeItem('real_refresh_token');
        dispatch({ type: 'CLEAR_USER' });
      });
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const url = await authApi.getGoogleAuthUrl();
    window.location.href = url;
  }, []);

  const handleGoogleCallback = useCallback(async (code: string) => {
    dispatch({ type: 'SET_LOADING' });
    const result = await authApi.googleCallback(code);
    localStorage.setItem('access_token', result.accessToken);
    localStorage.setItem('refresh_token', result.refreshToken);
    dispatch({ type: 'SET_USER', user: result.user });
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await authApi.logout(refreshToken);
      } catch {
        // Ignore logout API errors — clear tokens regardless
      }
    }
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('real_access_token');
    localStorage.removeItem('real_refresh_token');
    dispatch({ type: 'CLEAR_USER' });
  }, []);

  const impersonate = useCallback(
    async (userId: string) => {
      const result = await authApi.impersonateUser(userId);

      // Save current tokens as real tokens
      const currentAccessToken = localStorage.getItem('access_token');
      const currentRefreshToken = localStorage.getItem('refresh_token');
      if (currentAccessToken) localStorage.setItem('real_access_token', currentAccessToken);
      if (currentRefreshToken) localStorage.setItem('real_refresh_token', currentRefreshToken);

      // Store impersonated session token
      localStorage.setItem('access_token', result.accessToken);

      const realUser = state.user!;
      dispatch({ type: 'START_IMPERSONATION', impersonatedUser: result.user, realUser });
    },
    [state.user],
  );

  const stopImpersonation = useCallback(() => {
    const realAccessToken = localStorage.getItem('real_access_token');
    const realRefreshToken = localStorage.getItem('real_refresh_token');

    if (realAccessToken) localStorage.setItem('access_token', realAccessToken);
    if (realRefreshToken) localStorage.setItem('refresh_token', realRefreshToken);

    localStorage.removeItem('real_access_token');
    localStorage.removeItem('real_refresh_token');

    const realUser = state.realUser!;
    dispatch({ type: 'STOP_IMPERSONATION', realUser });
  }, [state.realUser]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        loginWithGoogle,
        handleGoogleCallback,
        logout,
        impersonate,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
